import { AppError, ErrorCode, ErrorSeverity } from '../types';
import { isCancelledAuthError } from '../../../utils/authErrors';

export function fromFirebaseAuthError(err: any): AppError {
  const code = String(err?.code ?? '');
  const message = err?.message || '';
  
  // Check for HTTP status codes and rate limit errors FIRST
  if (err?.status === 429 || code === 'RATE_LIMITED' || message.toLowerCase().includes('rate limit') || message.toLowerCase().includes('too many')) {
    return new AppError({ 
      code: ErrorCode.UNKNOWN, 
      domain: 'auth', 
      cause: err,
      message: message || 'Too many attempts. Please wait before trying again.',
      severity: ErrorSeverity.Warn
    });
  }
  
  // Handle redirect/popup cancellation errors (should be silent or low severity)
  if (isCancelledAuthError(err)) {
    return new AppError({ 
      code: ErrorCode.UNKNOWN, 
      domain: 'auth', 
      cause: err, 
      message: 'Sign-in was cancelled',
      severity: ErrorSeverity.Info // Low severity - user action, not an error
    });
  }
  
  // Preserve Firebase error codes in message
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/invalid-email':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return new AppError({ code: ErrorCode.AUTH_INVALID_CREDENTIALS, domain: 'auth', cause: err, message: code + ': ' + message });
    case 'auth/email-already-in-use':
      return new AppError({ code: ErrorCode.AUTH_EMAIL_IN_USE, domain: 'auth', cause: err, message: code + ': ' + message });
    case 'auth/weak-password':
      return new AppError({ code: ErrorCode.UNKNOWN, domain: 'auth', cause: err, message: code + ': ' + message });
    case 'auth/too-many-requests':
      return new AppError({ code: ErrorCode.UNKNOWN, domain: 'auth', cause: err, message: code + ': ' + message });
    case 'auth/user-disabled':
      return new AppError({ code: ErrorCode.AUTH_USER_DISABLED, domain: 'auth', cause: err, message: code + ': ' + message });
    case 'auth/requires-recent-login':
      return new AppError({ code: ErrorCode.AUTH_REQUIRES_RECENT_LOGIN, domain: 'auth', cause: err, message: code + ': ' + message });
    case 'auth/network-request-failed':
      return new AppError({ code: ErrorCode.AUTH_NETWORK, domain: 'auth', cause: err, message: code + ': ' + message });
    case 'auth/invalid-api-key':
      return new AppError({ 
        code: ErrorCode.UNKNOWN, 
        domain: 'auth', 
        cause: err, 
        message: 'Invalid Firebase API key. Please contact support.',
        severity: ErrorSeverity.Error
      });
    case 'auth/quota-exceeded':
      return new AppError({ 
        code: ErrorCode.UNKNOWN, 
        domain: 'auth', 
        cause: err, 
        message: 'Password reset quota exceeded. Please try again later.',
        severity: ErrorSeverity.Warn
      });
    case 'auth/internal-error':
      return new AppError({ 
        code: ErrorCode.UNKNOWN, 
        domain: 'auth', 
        cause: err, 
        message: 'An internal error occurred. Please try again later.',
        severity: ErrorSeverity.Error
      });
    case 'auth/account-exists-with-different-credential':
      return new AppError({ 
        code: ErrorCode.UNKNOWN, 
        domain: 'auth', 
        cause: err, 
        message: 'An account already exists with the same email address but different sign-in method. Please sign in using your original method.' 
      });
    case 'auth/operation-not-allowed':
      // This often means the Google provider is not enabled in Firebase Console
      return new AppError({ 
        code: ErrorCode.UNKNOWN, 
        domain: 'auth', 
        cause: err, 
        message: message.includes('Google') || message.includes('google.com') 
          ? 'Google sign-in is not enabled. Please contact support.' 
          : (code + ': ' + message)
      });
    default:
      // Preserve original error message (may be user-friendly like email verification)
      // If it's a Firebase error with a code, include the code; otherwise just use the message
      const errorMessage = code && code.startsWith('auth/') 
        ? `${code}: ${message || 'Authentication error occurred'}`
        : (message || 'Authentication error occurred');
      
      return new AppError({ 
        code: ErrorCode.UNKNOWN, 
        domain: 'auth', 
        cause: err, 
        severity: ErrorSeverity.Error,
        message: errorMessage
      });
  }
}
